import { EventEmitter, Injectable } from "@angular/core";
import { LoggerService } from "../../shared/services/logger.service";
import { NetworkStatusService } from "../../shared/services/network-status.service";

// Consecutive Failed statuses required before we treat the sync as actually failed (debounces flaky blips).
const FAILED_STATUS_THRESHOLD = 3;

// Cap on how many recently-completed step names we keep for the "last N steps" UI.
const MAX_COMPLETED_STEPS_HISTORY = 20;

export type SyncPhase = "fetch" | "send" | "apply";

// Name of the single flat item used to represent the blended fetch/send/apply fraction (see updatePhaseProgress).
const PHASE_BLEND_PROGRESS_ITEM = "DataSync:PhaseBlendProgress";
const PHASE_WEIGHTS_STORAGE_KEY = "bqa-sync-phase-weights";

// Starting assumption before any calibration data exists, roughly split evenly across the three phases.
const DEFAULT_PHASE_WEIGHTS: Record<SyncPhase, number> = {
  fetch: 0.35,
  send: 0.35,
  apply: 0.30
};

// Bounds each calibrated weight so one anomalous run (e.g. near-instant apply) can't collapse it to 0 or 1.
const MIN_PHASE_WEIGHT = 0.1;
const MAX_PHASE_WEIGHT = 0.7;

// EMA smoothing factor for recordPhaseDurations - higher reacts faster to recent runs, lower is steadier.
const PHASE_WEIGHT_EMA_ALPHA = 0.3;

export enum SyncStatus {
  InProgress,
  Complete,
  Failed,
  Incomplete
}

interface SyncProgressItem {
  description: string;
  // 0 = not started, 1 = complete. Fractional values only occur for PHASE_BLEND_PROGRESS_ITEM.
  completionFraction: number;
}

interface PhaseProgress {
  completed: number;
  total: number;
}

@Injectable({
  providedIn: "root",
})
export default class SyncStatusService {
  private activeSyncStatus: SyncStatus = SyncStatus.Complete;

  // Recent Failed statuses awaiting FAILED_STATUS_THRESHOLD confirmations before becoming "active".
  private syncFailedQueue: SyncStatus[] = [];

  // All in-flight progress items (uploads, fetches, wrapper steps, and the send/apply virtual item).
  private syncProgressItems: SyncProgressItem[] = [];

  // Rolling log of completed step descriptions, newest last, purely for the "recent steps" UI.
  private completedStepsHistory: string[] = [];

  // Raw completed/total counters for the fetch, send, and apply phases, independent of syncProgressItems.
  private phaseProgress: Record<SyncPhase, PhaseProgress> = {
    fetch: { completed: 0, total: 0 },
    send: { completed: 0, total: 0 },
    apply: { completed: 0, total: 0 }
  };

  // Phases actually touched (via updatePhaseProgress) during the current run - untouched phases
  // (e.g. "fetch" when nothing needs to be pulled first) are excluded from weighting so they don't
  // dilute/rubber-band the blended percentage.
  private touchedPhases = new Set<SyncPhase>();
  public isOnline: boolean = true;

  syncStatusChanged: EventEmitter<SyncStatus> = new EventEmitter();
  syncProgressChanged: EventEmitter<number> = new EventEmitter();

  constructor(private logger: LoggerService, private network: NetworkStatusService) {
    this.network.online$.subscribe(online => {
      this.isOnline = online;
      if (online === false) {
        this.flushAllSyncProgressItems();
      }
    });
  }

  public setSyncStatus(status: SyncStatus): void {
    let effectiveStatus: SyncStatus;
    if (status === SyncStatus.Failed) {
      this.syncFailedQueue.push(status);
      if (this.syncFailedQueue.length >= FAILED_STATUS_THRESHOLD) {
        effectiveStatus = SyncStatus.Failed;
        this.flushAllSyncProgressItems();
        this.logger.info(`[SyncStatusService] Sync status changed to Failed after ${this.syncFailedQueue.length} consecutive failed statuses.`);
      } else {
        this.logger.info(`[SyncStatusService] Sync status Failed received but waiting for more confirmations (${this.syncFailedQueue.length}/${FAILED_STATUS_THRESHOLD}), leaving it unchanged.`);
        effectiveStatus = this.activeSyncStatus;
      }
    } else {
      if (status === SyncStatus.Complete) {
        this.logger.info("[SyncStatusService] Sync status Complete received - resetting failed status queue.");
        this.syncFailedQueue = [];
        this.flushCompletedSyncProgressItems();
      }
      effectiveStatus = status;
    }

    if (this.activeSyncStatus !== effectiveStatus) {
      this.logger.info(`[SyncStatusService] Sync status changed: ${SyncStatus[effectiveStatus]}`);
    }

    this.activeSyncStatus = effectiveStatus;
    this.syncStatusChanged.emit(this.activeSyncStatus);
  }

  public getSyncStatus(): SyncStatus {
    return this.activeSyncStatus;
  }

  public getSyncStatusQueueLength(): number {
    return this.syncFailedQueue.length;
  }

  public addSyncProgressItem(description: string): void {
    if (this.syncProgressItems.some(i => i.description === description)) {
      this.logger.info(`[SyncStatusService] Sync progress item with description "${description}" already exists. Skipping addition.`);
      return;
    }

    this.syncProgressItems.push({ description, completionFraction: 0 });
    const progress = this.getSyncProgressPercentage();
    this.logger.info(`[SyncStatusService] Added sync progress item: ${description}. Current progress: ${progress}%`);
    this.syncProgressChanged.emit(progress);
  }

  public markSyncProgressItemComplete(description: string): void {
    const item = this.syncProgressItems.find(i => i.description === description);
    if (item && item.completionFraction < 1) {
      item.completionFraction = 1;
      this.recordCompletedStep(description);
      const progress = this.getSyncProgressPercentage();
      this.logger.info(`[SyncStatusService] Marked sync progress item complete: ${description}. Current progress: ${progress}%`);
      this.syncProgressChanged.emit(progress);
    }
  }

  public flushAllSyncProgressItems(): void {
    this.syncProgressItems = [];
    this.completedStepsHistory = [];
    this.resetPhaseProgress();
    this.logger.info("[SyncStatusService] Flushed all sync progress items.");
    this.syncProgressChanged.emit(this.getSyncProgressPercentage());
  }

  public flushCompletedSyncProgressItems(): void {
    this.syncProgressItems = this.syncProgressItems.filter(item => item.completionFraction < 1);
    this.completedStepsHistory = [];
    this.logger.info("[SyncStatusService] Flushed completed sync progress items.");
    this.syncProgressChanged.emit(this.getSyncProgressPercentage());
  }

  public containsSyncProgressItem(description: string): boolean {
    return this.syncProgressItems.some(i => i.description.includes(description));
  }

  public getRecentCompletedSteps(count: number = 5): string[] {
    return this.completedStepsHistory.slice(-count).reverse();
  }

  // Records a named step in the "recently completed" history without affecting the % calculation -
  // used for send/apply phase batches, whose completion is tracked via updatePhaseProgress instead.
  public recordCompletedStep(description: string): void {
    this.completedStepsHistory.push(description);
    if (this.completedStepsHistory.length > MAX_COMPLETED_STEPS_HISTORY) {
      this.completedStepsHistory.shift();
    }
  }

  // Resets the fetch/send/apply phase counters and the blended progress item's fraction for a new sync run.
  public resetPhaseProgress(): void {
    this.phaseProgress = {
      fetch: { completed: 0, total: 0 },
      send: { completed: 0, total: 0 },
      apply: { completed: 0, total: 0 }
    };
    this.touchedPhases.clear();
    const item = this.syncProgressItems.find(i => i.description === PHASE_BLEND_PROGRESS_ITEM);
    if (item) {
      item.completionFraction = 0;
    }
  }

  // Reports progress for the "fetch" (pre-sync data pull), "send" (upload batches), or "apply"
  // (local database update batches) phase of a data sync. Phases are blended into a single weighted
  // fraction (instead of being registered as many individual flat items) so a phase whose batch count
  // isn't known upfront (apply), or one that doesn't run every sync (fetch), can't cause the overall
  // percentage to jump backward/dilute once it's added or its real batch count is discovered.
  public updatePhaseProgress(phase: SyncPhase, completed: number, total: number): void {
    this.phaseProgress[phase] = { completed, total };
    this.touchedPhases.add(phase);
    if (!this.syncProgressItems.some(i => i.description === PHASE_BLEND_PROGRESS_ITEM)) {
      this.syncProgressItems.push({ description: PHASE_BLEND_PROGRESS_ITEM, completionFraction: 0 });
    }

    const item = this.syncProgressItems.find(i => i.description === PHASE_BLEND_PROGRESS_ITEM)!;
    const fraction = this.getPhaseWeightedFraction();
    // Ratchet: this item's fraction only ever moves forward within a run, even if a phase's total is
    // later revised (e.g. the apply phase's batch count reconciled against the real server response).
    item.completionFraction = Math.max(item.completionFraction, fraction);

    const progress = this.getSyncProgressPercentage();
    this.logger.info(`[SyncStatusService] Updated ${phase} phase progress: ${completed}/${total}. Overall progress: ${progress}%`);
    this.syncProgressChanged.emit(progress);
  }

  // Self-calibrating weights (summing to ~1) for how much of the blended fraction each phase
  // represents, persisted locally and nudged toward each run's real observed timing split.
  private getPhaseWeights(): Record<SyncPhase, number> {
    let stored: Partial<Record<SyncPhase, number>> = {};
    try {
      const raw = localStorage.getItem(PHASE_WEIGHTS_STORAGE_KEY);
      stored = raw ? JSON.parse(raw) : {};
    } catch {
      // localStorage unavailable/corrupt (e.g. private browsing) - fall back to the defaults below.
    }

    const weights = { ...DEFAULT_PHASE_WEIGHTS };
    (Object.keys(DEFAULT_PHASE_WEIGHTS) as SyncPhase[]).forEach(phase => {
      const value = stored[phase];
      if (typeof value === "number" && Number.isFinite(value) && value >= MIN_PHASE_WEIGHT && value <= MAX_PHASE_WEIGHT) {
        weights[phase] = value;
      }
    });
    return weights;
  }

  // durations should only include the phases that actually ran this sync (e.g. omit "fetch" when
  // nothing needed to be pulled first) so untouched phases don't skew their stored weight.
  public recordPhaseDurations(durations: Partial<Record<SyncPhase, number>>): void {
    const totalMs = Object.values(durations).reduce((sum: number, ms) => sum + (ms ?? 0), 0);
    if (totalMs <= 0) {
      return;
    }

    const currentWeights = this.getPhaseWeights();
    const updatedWeights = { ...currentWeights };
    (Object.keys(durations) as SyncPhase[]).forEach(phase => {
      const ms = durations[phase];
      if (ms === undefined) {
        return;
      }
      const observedWeight = Math.min(MAX_PHASE_WEIGHT, Math.max(MIN_PHASE_WEIGHT, ms / totalMs));
      updatedWeights[phase] = (currentWeights[phase] * (1 - PHASE_WEIGHT_EMA_ALPHA)) + (observedWeight * PHASE_WEIGHT_EMA_ALPHA);
    });

    try {
      localStorage.setItem(PHASE_WEIGHTS_STORAGE_KEY, JSON.stringify(updatedWeights));
    } catch {
      // localStorage unavailable - calibration simply won't persist for future runs.
    }
  }

  // Blends only the phases actually touched this run, renormalizing their weights to sum to 1 so an
  // inapplicable phase (e.g. "fetch" when nothing needed pulling) doesn't drag down the fraction.
  private getPhaseWeightedFraction(): number {
    const phases = Array.from(this.touchedPhases);
    if (phases.length === 0) {
      return 0;
    }

    const weights = this.getPhaseWeights();
    const totalWeight = phases.reduce((sum, phase) => sum + weights[phase], 0);

    return phases.reduce((fraction, phase) => {
      const progress = this.phaseProgress[phase];
      const ratio = progress.total > 0 ? progress.completed / progress.total : 1;
      const normalizedWeight = totalWeight > 0 ? weights[phase] / totalWeight : 1 / phases.length;
      return fraction + (ratio * normalizedWeight);
    }, 0);
  }

  private getSyncProgressPercentage(): number {
    if (this.syncProgressItems.length === 0) {
      return 100;
    }
    const totalFraction = this.syncProgressItems.reduce((sum, item) => sum + item.completionFraction, 0);
    return Math.floor((totalFraction / this.syncProgressItems.length) * 100);
  }
}